import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

import pathnames from "routes/pathnames";

import api from "services/api";

import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import REGEX from "constants/regex";
import ERRORS from "constants/errors";
import CONSTANTS from "constants/constant";
import { statusOptions } from "constants/status-options";
import { yesNoOptions } from "constants/yes-no-options";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import { PageOfficeLocationTableDto } from "dto/pages/office-location/page-office-location-list";

import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppHeading from "components/app-heading";
import AppBreadcrumbs from "components/app-breadcrumbs";
import AppSelectInput from "components/app-select-input";
import countries from "common/countries";

const PageOfficeLocationInfo = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const isAddNew = useMemo(() => (id === ActionEnum.Create ? true : false), [id]);
	const buttonLabel = useMemo(() => (isAddNew ? "Add" : "Save"), [isAddNew]);
	const countryOptions = countries.map((country) => ({
		label: country.name,
		value: country.name,
	}));

	//prettier-ignore
	const breadcrumbItems = useMemo(() => [
        { label: "Office Location", href: pathnames.officeLocation },
        { label: isAddNew ? "Add New" : "Edit Location", href: isAddNew ? pathnames.officeLocation + "/create" : `${pathnames.officeLocation}/${id}` },
    ], [id, isAddNew]);

	const validationSchema = yup.object({
		headquarter: yup.boolean().required(ERRORS.REQUIRED),
		location: yup.string().required(ERRORS.REQUIRED),
		status: yup.string().required(ERRORS.REQUIRED),
		contactEmail: yup.string().matches(REGEX.EMAIL, ERRORS.EMAIL).required(ERRORS.REQUIRED),
		contactNumber: yup.string().required(ERRORS.REQUIRED),
		fullAddress: yup.object({
			address1: yup.string().required(ERRORS.REQUIRED),
			address2: yup.string().required(ERRORS.REQUIRED),
			address3: yup.string(),
			postcode: yup.string().when("country", {
				is: (country: string) => country === "Malaysia",
				then: (schema) => schema.required(ERRORS.REQUIRED),
				otherwise: (schema) => schema.nullable(),
			}),
			region: yup.string().when("country", {
				is: (country: string) => country === "Malaysia",
				then: (schema) => schema.required(ERRORS.REQUIRED),
				otherwise: (schema) => schema.nullable(),
			}),
			state: yup.string().when("country", {
				is: (country: string) => country === "Malaysia",
				then: (schema) => schema.required(ERRORS.REQUIRED),
				otherwise: (schema) => schema.nullable(),
			}),
			country: yup.string().required(ERRORS.REQUIRED),
		}),
	});

	const formik = useFormik<PageOfficeLocationTableDto>({
		initialValues: { id: "", headquarter: true, location: "", status: "", contactEmail: "", contactNumber: "", fullAddress: { address1: "", address2: "", address3: "", postcode: "", region: "", state: "", country: "" } },
		validationSchema,
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values: PageOfficeLocationTableDto) => {
        let response = null;

        try {
            const payload = {
                id: values.id || "",
                isHeadquarter: values.headquarter,
                location: values.location || "",
                status: values.status || "",
                contactEmail: values.contactEmail || "",
                contactNumber: values.contactNumber || "",
                fullAddress: {
                    address1: values.fullAddress.address1 || "",
                    address2: values.fullAddress.address2 || "",
                    address3: values.fullAddress.address3 || "",
                    postcode: values.fullAddress.postcode || "",
                    region: values.fullAddress.region || "",
                    state: values.fullAddress.state || "",
                    country: values.fullAddress.country || "",
                },
            };

            if (isAddNew) {
                response = await api.post.location.create(payload);
            } else if (values.id) {
                response = await api.put.location.update(payload);
            }
        } catch (error) {
            const err = error as Error | ErrorResponseDto;

            serveRequestErrors(err);
        }

        if (response) {
            navigate(pathnames.officeLocation);

            if (isAddNew) serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);
            else serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
        }
	}, [isAddNew, navigate]);

	const onHandleGetArticleDetails = useCallback(async () => {
		try {
			const response = await api.get.location.location({ id });

			const locationInfo = response.data.data;

			formik.setValues({
				id: locationInfo.id || "",
				headquarter: locationInfo.headquarter ?? true,
				location: locationInfo.location || "",
				status: locationInfo.status || "",
				contactEmail: locationInfo.contactEmail || "",
				contactNumber: locationInfo.contactNumber || "",
				fullAddress: {
					address1: locationInfo.fullAddress.address1 || "",
					address2: locationInfo.fullAddress.address2 || "",
					address3: locationInfo.fullAddress.address3 || "",
					postcode: locationInfo.fullAddress.postcode || "",
					region: locationInfo.fullAddress.region || "",
					state: locationInfo.fullAddress.state || "",
					country: locationInfo.fullAddress.country || "",
				},
			});
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, formik.setValues]);

	useEffect(() => {
		if (!isAddNew) onHandleGetArticleDetails();
	}, [isAddNew, onHandleGetArticleDetails]);

	return (
		<div className="page-location-info">
			<div className="location">
				<AppHeading title="Office Location">
					<AppHeading.Divider />
				</AppHeading>

				<form className="location__container" onSubmit={formik.handleSubmit}>
					<div className="location__header">
						<AppBreadcrumbs items={breadcrumbItems} />

						<div className="location__button-wrapper">
							<AppButton type="button" label="Cancel" disabled={formik.isSubmitting} text onClick={() => navigate(pathnames.officeLocation)} />

							<AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
						</div>
					</div>

					<div className="form">
						<p className="form__title">Location Setting</p>

						<div className="form__wrapper">
							{/* prettier-ignore */}
							<AppSelectInput type="text" name="headquarter" label="Headquarter" placeholder="Please select" value={formik.values.headquarter.toString()} touched={formik.touched.headquarter} error={formik.errors.headquarter} options={yesNoOptions} onChange={(e) => {formik.setFieldValue("headquarter", e.target.value === "true");}} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="location" label="Location" placeholder="Location" value={formik.values.location} touched={formik.touched.location} error={formik.errors.location} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="status" label="Status" placeholder="Please select" value={formik.values.status} touched={formik.touched.status} error={formik.errors.status} options={statusOptions} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="contactEmail" label="Contact Email" placeholder="abc@email.com" value={formik.values.contactEmail} touched={formik.touched.contactEmail} error={formik.errors.contactEmail} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<div className="full-width">
								<AppInput type="text" name="contactNumber" label="Location Link" placeholder="Enter Location Url" value={formik.values.contactNumber} touched={formik.touched.contactNumber} error={formik.errors.contactNumber} onChange={formik.handleChange} required />
							</div>
						</div>

						<p className="form__subtitle">Full Address</p>

						<div className="form__wrapper">
							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.address1" label="Address Line 1" placeholder="Address Line 1" value={formik.values.fullAddress.address1} touched={formik.touched.fullAddress?.address1} error={formik.errors.fullAddress?.address1} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.address2" label="Address Line 2" placeholder="Address Line 2" value={formik.values.fullAddress.address2} touched={formik.touched.fullAddress?.address2} error={formik.errors.fullAddress?.address2} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.address3" label="Address Line 3" placeholder="Address Line 3" value={formik.values.fullAddress.address3} touched={formik.touched.fullAddress?.address3} error={formik.errors.fullAddress?.address3} onChange={formik.handleChange}  />

							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.postcode" label="Postal/Zip Code" placeholder="55100" value={formik.values.fullAddress.postcode} touched={formik.touched.fullAddress?.postcode} error={formik.errors.fullAddress?.postcode} onChange={formik.handleChange} required={formik.values.fullAddress.country === "Malaysia"} />

							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.region" label="City" placeholder="Puchong" value={formik.values.fullAddress.region} touched={formik.touched.fullAddress?.region} error={formik.errors.fullAddress?.region} onChange={formik.handleChange} required={formik.values.fullAddress.country === "Malaysia"} />

							{/* prettier-ignore */}
							<AppInput type="text" name="fullAddress.state" label="State/Province/Region" placeholder="Selangor" value={formik.values.fullAddress.state} touched={formik.touched.fullAddress?.state} error={formik.errors.fullAddress?.state} onChange={formik.handleChange} required={formik.values.fullAddress.country === "Malaysia"} />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="fullAddress.country" label="Country" placeholder={formik.values.fullAddress.country} value={formik.values.fullAddress.country} touched={formik.touched.fullAddress?.country} error={formik.errors.fullAddress?.country} options={countryOptions} onChange={formik.handleChange} required />
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default PageOfficeLocationInfo;
